import { sectorData } from '../config/sectorData';

export const calculateTotalImpact = (sectorImpact) => {
  // Base projection starting point (in MtCO₂/year)
  const baselineEmissions = 450; // UK's 2020 emissions level
  
  // Emissions reduction multipliers for each sector
  const sectorReductionMultipliers = {
    food: {
      current: 1.0,
      low: 0.85,
      high: 0.7
    },
    buildings: {
      current: 1.0,
      low: 0.75,
      high: 0.6
    },
    transport: {
      current: 1.0,
      low: 0.75,
      high: 0.5
    },
    industry: {
      current: 1.0,
      low: 0.8,
      high: 0.65
    }
  };

  // Validate input
  const safeSectorImpact = {
    food: ['current', 'low', 'high'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
    buildings: ['current', 'low', 'high'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
    transport: ['current', 'low', 'high'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
    industry: ['current', 'low', 'high'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
  };

  // Calculate cumulative reduction factor
  const cumulativeReductionFactor = Object.keys(sectorReductionMultipliers).reduce((acc, sector) => {
    return acc * sectorReductionMultipliers[sector][safeSectorImpact[sector]];
  }, 1);
  
  // Calculate final emissions in 2050
  const finalEmissions = baselineEmissions * cumulativeReductionFactor;
  
  // Calculate percentage reduction
  const reductionPercentage = Math.round((1 - finalEmissions / baselineEmissions) * 100);

  return reductionPercentage;
};

export const calculateDetailedTradeoffBalance = (sectorImpact) => {
  // Base values when all sectors are at low impact
  const baseValues = {
    energyDemand: 80,        // Higher when choices are low
    emissions: 75,           // Higher when choices are low
    greenEnergySupply: 40,   // Lower when choices are low
    carbonRemoval: 60        // Higher when choices are low
  };

  // Ensure sectorImpact is an object with expected keys
  const safeSectorImpact = {
    food: sectorImpact?.food || 'low',
    buildings: sectorImpact?.buildings || 'low',
    transport: sectorImpact?.transport || 'low',
    industry: sectorImpact?.industry || 'low'
  };

  // Impact modifiers for each sector
  const sectorModifiers = {
    food: {
      energyDemand: safeSectorImpact.food === 'high' ? -15 : 0,
      emissions: safeSectorImpact.food === 'high' ? -20 : 0,
      greenEnergySupply: safeSectorImpact.food === 'high' ? -10 : 0,
      carbonRemoval: safeSectorImpact.food === 'high' ? -15 : 0
    },
    buildings: {
      energyDemand: safeSectorImpact.buildings === 'high' ? -20 : 0,
      emissions: safeSectorImpact.buildings === 'high' ? -25 : 0,
      greenEnergySupply: safeSectorImpact.buildings === 'high' ? -15 : 0,
      carbonRemoval: safeSectorImpact.buildings === 'high' ? -20 : 0
    },
    transport: {
      energyDemand: safeSectorImpact.transport === 'high' ? -25 : 0,
      emissions: safeSectorImpact.transport === 'high' ? -30 : 0,
      greenEnergySupply: safeSectorImpact.transport === 'high' ? -20 : 0,
      carbonRemoval: safeSectorImpact.transport === 'high' ? -25 : 0
    },
    industry: {
      energyDemand: safeSectorImpact.industry === 'high' ? -15 : 0,
      emissions: safeSectorImpact.industry === 'high' ? -20 : 0,
      greenEnergySupply: safeSectorImpact.industry === 'high' ? -10 : 0,
      carbonRemoval: safeSectorImpact.industry === 'high' ? -15 : 0
    }
  };

  // Calculate modified values
  return {
    energyDemand: Math.max(20, Math.min(90, baseValues.energyDemand + 
      sectorModifiers.food.energyDemand + 
      sectorModifiers.buildings.energyDemand + 
      sectorModifiers.transport.energyDemand + 
      sectorModifiers.industry.energyDemand
    )),
    emissions: Math.max(10, Math.min(90, baseValues.emissions + 
      sectorModifiers.food.emissions + 
      sectorModifiers.buildings.emissions + 
      sectorModifiers.transport.emissions + 
      sectorModifiers.industry.emissions
    )),
    greenEnergySupply: Math.max(20, Math.min(90, baseValues.greenEnergySupply + 
      sectorModifiers.food.greenEnergySupply + 
      sectorModifiers.buildings.greenEnergySupply + 
      sectorModifiers.transport.greenEnergySupply + 
      sectorModifiers.industry.greenEnergySupply
    )),
    carbonRemoval: Math.max(10, Math.min(90, baseValues.carbonRemoval + 
      sectorModifiers.food.carbonRemoval + 
      sectorModifiers.buildings.carbonRemoval + 
      sectorModifiers.transport.carbonRemoval + 
      sectorModifiers.industry.carbonRemoval
    ))
  };
};

export const generateNetZeroEquationExplanation = (sectorImpact) => {
  // Ensure sectorImpact is an object with expected keys
  const safeSectorImpact = {
    food: sectorImpact?.food || 'low',
    buildings: sectorImpact?.buildings || 'low',
    transport: sectorImpact?.transport || 'low',
    industry: sectorImpact?.industry || 'low'
  };

  // Comprehensive explanation matrix
  const explanationMatrix = {
    'low-low-low-low': "Your current choices suggest a high-carbon pathway. Low ambition across all sectors means significantly higher energy demand, substantial emissions, and a critical need for green energy infrastructure and carbon removal technologies.",

    'high-high-high-high': "You've chosen an optimal path to net zero. Comprehensive high-impact choices dramatically reduce energy demand, minimize emissions, and significantly lower the need for green energy infrastructure and carbon removal technologies.",

    // Add other combinations as before...
    
    // Default explanation
    'default': "Your choices present a complex path to net zero, requiring careful balancing of energy demand, emissions, green energy infrastructure, and carbon removal technologies."
  };

  // Generate the key for the current combination
  const currentKey = `${safeSectorImpact.food}-${safeSectorImpact.buildings}-${safeSectorImpact.transport}-${safeSectorImpact.industry}`;

  // Return the explanation, falling back to default if not found
  return explanationMatrix[currentKey] || explanationMatrix['default'];
}; 